import axios, { type AxiosInstance, type AxiosError } from 'axios';
import type { ApiError } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Cross-subdomain SSO cookie, written by sso-portal (login.moyasolutions.com)
// with Domain=.moyasolutions.com. It is not httpOnly -- it cannot be, because
// it is set from JS -- so every Moya app can read it, and the inline bootstrap
// in index.html rehydrates localStorage from it on every page load.
//
// That means clearing localStorage alone does NOT end the session: the very
// next page load copies the token straight back out of the cookie. Anything
// that tears down the local session must clear the cookie too.
const SSO_COOKIE_NAME = 'moya_token';
const SSO_COOKIE_DOMAIN = '.moyasolutions.com';

/**
 * Clear every client-side trace of the session: the localStorage keys this app
 * uses, the legacy keys the index.html bootstrap still writes, and the
 * domain-wide SSO cookie.
 *
 * The cookie is deleted twice -- once scoped to the shared parent domain to
 * match how sso-portal sets it, and once host-scoped -- because a delete only
 * takes effect when its Domain and Path match the cookie being removed, and a
 * host-scoped copy may exist from an older build.
 */
export function clearSsoSession(): void {
  localStorage.removeItem('moya_token');
  localStorage.removeItem('moya_user');
  // Legacy keys still written by the index.html cookie-hydration bootstrap.
  localStorage.removeItem('token');
  localStorage.removeItem('user_id');

  const expire = `${SSO_COOKIE_NAME}=; Path=/; Max-Age=0; Secure; SameSite=Lax`;
  document.cookie = `${expire}; Domain=${SSO_COOKIE_DOMAIN}`;
  document.cookie = expire;
}

/**
 * Is the locally-held JWT past its own `exp`?
 *
 * Decode only -- no signature check, which would be pointless client-side.
 * An unreadable or `exp`-less token counts as expired: we cannot vouch for it,
 * and treating it as live would leave the user stuck in a redirect loop.
 */
function localTokenIsExpired(): boolean {
  const token = localStorage.getItem(SSO_COOKIE_NAME);
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? ''));
    if (typeof payload.exp !== 'number') return true;
    return payload.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

/**
 * A bare axios instance with NO interceptors, used only to ask identity-service
 * whether the session itself is still good. It must not carry the response
 * interceptor below, or a 401 from this very check would re-enter it and
 * recurse.
 */
const sessionProbe = axios.create({ baseURL: API_BASE_URL });

let sessionProbeInFlight: Promise<boolean> | null = null;

/**
 * Decide whether a 401 means "your session is over" or "that particular call
 * was refused".
 *
 * This distinction matters because ending the session clears a cookie scoped to
 * .moyasolutions.com -- the SHARED SSO session. Clearing it on any 401 would
 * sign the user out of the tickets portal, the console and every other open tab
 * because one admin-portal request touched an endpoint their role does not
 * cover. identity-service now returns 401 on 54 routes that previously answered
 * openly, and the permission codes to grant against only became grantable
 * today, so endpoint-specific 401s are expected rather than exceptional.
 *
 * Order matters, cheapest first:
 *   1. No token, or past its own exp -> dead, no round-trip needed.
 *   2. Otherwise ask /auth/me once. Still valid -> the 401 was about that one
 *      request, so leave the shared session alone. Also 401 -> genuinely dead
 *      (revoked, or signed with a rotated key), so tear down.
 *
 * Concurrent 401s share one in-flight probe: a page issuing five parallel
 * requests must not fire five probes.
 */
async function sessionIsDead(): Promise<boolean> {
  if (localTokenIsExpired()) return true;

  if (!sessionProbeInFlight) {
    const token = localStorage.getItem(SSO_COOKIE_NAME);
    sessionProbeInFlight = sessionProbe
      .get('/auth/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(() => false)
      .catch((err: AxiosError) => err.response?.status === 401)
      .finally(() => {
        sessionProbeInFlight = null;
      });
  }
  return sessionProbeInFlight;
}

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('moya_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiError>) => {
        if (error.response?.status === 401) {
          // Confirm the SESSION is dead before tearing it down. clearSsoSession()
          // expires a .moyasolutions.com cookie -- the shared SSO session -- so
          // doing it on every 401 would sign the user out of the tickets portal,
          // the console and every other open tab because one request here hit an
          // endpoint their role does not cover.
          //
          // Clearing the cookie is still required when the session really has
          // ended: localStorage alone is not enough, because index.html's
          // bootstrap copies the same rejected token back out of the cookie on
          // the next load and we bounce off 401 in a loop.
          //
          // Fire-and-forget: the original error is rejected immediately either
          // way, so the caller's own error handling is unaffected.
          void sessionIsDead().then((dead) => {
            if (!dead) return;
            clearSsoSession();
            const ssoUrl = import.meta.env.VITE_SSO_URL || 'https://login.moyasolutions.com';
            const callbackUrl = `${window.location.origin}/callback`;
            window.location.href = `${ssoUrl}/login?redirect_uri=${encodeURIComponent(callbackUrl)}`;
          });
        }
        return Promise.reject(error);
      }
    );
  }

  setToken(token: string) {
    localStorage.setItem('moya_token', token);
  }

  clearToken() {
    clearSsoSession();
  }

  // Generic request methods
  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    const response = await this.client.get<T>(url, { params });
    return response.data;
  }

  async getBlob(url: string, params?: Record<string, unknown>): Promise<Blob> {
    const response = await this.client.get(url, { params, responseType: 'blob' });
    return response.data;
  }

  async post<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.post<T>(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.put<T>(url, data);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.client.patch<T>(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url);
    return response.data;
  }
}

export const api = new ApiService();
export default api;
